import {
  ButtonGroup,
  ClickAwayListener,
  Color,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  PopperPlacementType,
  Stack,
  Typography,
} from '@mui/material'
import { NestedMenuItem } from 'mui-nested-menu'
import { useEffect, useRef, useState } from 'react'
import { useMinimalsTheme } from '../../../hooks'
import { ChevronLeftIcon, ChevronRightIcon } from '../../icons'
import { MoreOptionsButton } from './MoreOptionsButton'
import { ButtonOption, ButtonSize } from './button.model'

interface RecursiveProps {
  options: ButtonOption[]
  open: boolean
  handleClickCallback: (option: ButtonOption) => void
}
const RecursiveMenuItems = (props: RecursiveProps) => {
  return (
    <>
      {props.options.map(option =>
        option?.children && option.children.length > 0 ? (
          <NestedMenuItem
            onClick={event => {
              event.stopPropagation()
              props.handleClickCallback(option)
            }}
            key={option.label.split(' ').join('_')}
            label={option.label}
            data-cy={option.testId}
            parentMenuOpen={props.open}
            divider={option.divider}
            leftIcon={option.openToLeft ? <ChevronLeftIcon /> : undefined}
            rightIcon={option.openToLeft ? <></> : <ChevronRightIcon />}
            MenuProps={{
              disableScrollLock: true,
              anchorOrigin: {
                vertical: 'top',
                horizontal: option.openToLeft ? 'left' : 'right',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: option.openToLeft ? 'right' : 'left',
              },
            }}
          >
            <RecursiveMenuItems
              options={option.children}
              open={props.open}
              handleClickCallback={props.handleClickCallback}
            />
          </NestedMenuItem>
        ) : (
          <MenuItem
            data-cy={option.testId}
            key={option.label.split(' ').join('_')}
            onClick={event => {
              event.stopPropagation()
              props.handleClickCallback(option)
            }}
            divider={option.divider}
            disabled={option.disabled}
            sx={{ display: 'list-item', width: '100%' }}
          >
            <Stack
              direction='row'
              ml='-4px'
              alignItems='center'
              justifyContent='space-between'
              gap={2}
            >
              <Stack>
                <Typography color={option.color}>{option.label}</Typography>
                {option.subtitle && (
                  <Typography variant='caption' color='gray'>
                    {option.subtitle}
                  </Typography>
                )}
              </Stack>
              {option.rightIcon}
            </Stack>
          </MenuItem>
        ),
      )}
    </>
  )
}

const optionsMenuBus = new EventTarget()

interface Props {
  options: ButtonOption[]
  fill?: string | Color
  noPadding?: boolean
  position?: PopperPlacementType
  size?: ButtonSize
  tooltip?: string
  testId?: string
  customIcon?: React.ReactNode
}
export const OptionsButton = ({
  options,
  position,
  size,
  tooltip,
  testId,
  fill,
  noPadding,
  customIcon,
}: Props) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const idRef = useRef(Symbol('options-button')) // unique id per instance
  const theme = useMinimalsTheme()

  // Listen for "closeAll" events so this menu closes when another opens
  useEffect(() => {
    const handleCloseAll = (e: Event) => {
      if ((e as CustomEvent).detail !== idRef.current) {
        setOpen(false)
      }
    }
    optionsMenuBus.addEventListener('closeAll', handleCloseAll)
    return () => optionsMenuBus.removeEventListener('closeAll', handleCloseAll)
  }, [])

  const handleMenuItemClick = (option: ButtonOption) => {
    if (option.onClick) option.onClick()
    setOpen(false)
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!open) {
      optionsMenuBus.dispatchEvent(new CustomEvent('closeAll', { detail: idRef.current }))
    }

    setOpen(prev => !prev)
  }

  const handleClose = (event: Event) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) return
    setOpen(false)
  }

  return (
    <>
      <ButtonGroup ref={anchorRef}>
        <MoreOptionsButton
          testId={testId}
          size={size}
          fill={fill}
          noPadding={noPadding}
          tooltip={tooltip ?? ''}
          onClick={handleToggle}
          customIcon={customIcon}
        />
      </ButtonGroup>
      <Popper
        sx={{ zIndex: theme.zIndex.modal + 1 }}
        open={open}
        placement={position ?? 'left'}
        anchorEl={anchorRef.current}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper elevation={4}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='split-button-menu' autoFocusItem>
                  <RecursiveMenuItems
                    options={options}
                    open={open}
                    handleClickCallback={handleMenuItemClick}
                  />
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}
